import {DraggableHandle} from '@wandb/weave/components/DraggablePopups';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useMemo} from 'react';
import {createPortal} from 'react-dom';
import type {DraggableEventHandler} from 'react-draggable';
import {Rnd, type RndResizeCallback} from 'react-rnd';

import {useNavbarBottom} from '../../util/hooks/useNavbarBottom';
import {
  MAX_WINDOW_HEIGHT,
  MAX_WINDOW_WIDTH,
  MIN_WINDOW_HEIGHT,
  MIN_WINDOW_WIDTH,
} from './constants';
import {useBoundedPlacement} from './useBoundedPlacement';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {useViewportHeight} from './useViewportHeight';
import {useWBAgentContextStore} from './WBAgentContextStore';
import {WBAgentHeader} from './WBAgentHeader';
import {WBAgentThread} from './WBAgentThread';

const CORNER_HANDLE_AREA_EDGE = 20;
const CORNER_HANDLE_AREA_INSET = -(CORNER_HANDLE_AREA_EDGE / 2);
const EDGE_HANDLE_DIM = 5;
const EDGE_HANDLE_INSET = -EDGE_HANDLE_DIM / 2;

const RESIZE_HANDLE_STYLES = {
  bottomLeft: {
    width: CORNER_HANDLE_AREA_EDGE,
    height: CORNER_HANDLE_AREA_EDGE,
    bottom: CORNER_HANDLE_AREA_INSET,
    left: CORNER_HANDLE_AREA_INSET,
  },
  bottomRight: {
    width: CORNER_HANDLE_AREA_EDGE,
    height: CORNER_HANDLE_AREA_EDGE,
    bottom: CORNER_HANDLE_AREA_INSET,
    right: CORNER_HANDLE_AREA_INSET,
  },
  topLeft: {
    width: CORNER_HANDLE_AREA_EDGE,
    height: CORNER_HANDLE_AREA_EDGE,
    top: CORNER_HANDLE_AREA_INSET,
    left: CORNER_HANDLE_AREA_INSET,
  },
  topRight: {
    width: CORNER_HANDLE_AREA_EDGE,
    height: CORNER_HANDLE_AREA_EDGE,
    top: CORNER_HANDLE_AREA_INSET,
    right: CORNER_HANDLE_AREA_INSET,
  },
  bottom: {height: EDGE_HANDLE_DIM, bottom: EDGE_HANDLE_INSET},
  top: {height: EDGE_HANDLE_DIM, top: EDGE_HANDLE_INSET},
  right: {width: EDGE_HANDLE_DIM, right: EDGE_HANDLE_INSET},
  left: {width: EDGE_HANDLE_DIM, left: EDGE_HANDLE_INSET},
} as const;

export const WBAgentChatWindow = () => {
  const nightModeRevertFilter = useNightModeRevertFilter();
  const navbarBottom = useNavbarBottom();
  const desiredWidth = useWBAgentContextStore(s => s.windowWidth);
  const desiredHeight = useWBAgentContextStore(s => s.windowHeight);
  const setWindowSize = useWBAgentContextStore(s => s.setWindowSize);
  const position = useWBAgentContextStore(s => s.windowPosition);
  const setPosition = useWBAgentContextStore(s => s.setWindowPosition);
  const viewportHeight = useViewportHeight();

  const availableHeight = viewportHeight - navbarBottom;
  const effectiveHeight = Math.max(
    MIN_WINDOW_HEIGHT,
    Math.min(desiredHeight, availableHeight)
  );
  const {containerRef} = useBoundedPlacement(desiredWidth, effectiveHeight);

  const maxConstraints: {width: number; height: number} = useMemo(
    () => ({
      width: Math.min(MAX_WINDOW_WIDTH, window.innerWidth * 0.75),
      height: Math.min(MAX_WINDOW_HEIGHT, availableHeight),
    }),
    [availableHeight]
  );

  const size = useMemo(
    () => ({width: desiredWidth, height: effectiveHeight}),
    [desiredWidth, effectiveHeight]
  );

  const handleDragStop = useCallback<DraggableEventHandler>(
    (_e, d) => setPosition({x: d.x, y: d.y}),
    [setPosition]
  );

  const handleResize = useCallback<RndResizeCallback>(
    (_e, _dir, el, _delta, pos) => {
      setWindowSize(el.offsetWidth, el.offsetHeight);
      setPosition(pos);
    },
    [setWindowSize, setPosition]
  );

  return createPortal(
    <Tailwind>
      <div
        ref={containerRef}
        className="fixed inset-x-0 bottom-0 z-[1000] pointer-events-none overflow-hidden"
        style={{top: navbarBottom}}>
        <Rnd
          size={size}
          position={position ?? undefined}
          className="pointer-events-auto rounded-[8px] flex flex-col"
          minWidth={MIN_WINDOW_WIDTH}
          minHeight={MIN_WINDOW_HEIGHT}
          maxWidth={maxConstraints.width}
          maxHeight={maxConstraints.height}
          dragHandleClassName="handle"
          resizeHandleStyles={RESIZE_HANDLE_STYLES}
          bounds="parent"
          onDragStop={handleDragStop}
          onResize={handleResize}>
          <div
            className="absolute inset-0 rounded-[8px] bg-white dark:bg-moon-850 border-[1.5px] border-moon-250 dark:border-[#363C44] shadow-[0_12px_24px_0_rgba(13,15,18,0.16)] dark:shadow-[0_12px_24px_0_rgba(13,15,18,0.32)]"
            style={{filter: nightModeRevertFilter}}
          />
          <div
            className="relative flex flex-col"
            style={{width: desiredWidth, height: effectiveHeight}}>
            <DraggableHandle>
              <WBAgentHeader />
            </DraggableHandle>
            <WBAgentThread />
          </div>
        </Rnd>
      </div>
    </Tailwind>,
    document.body
  );
};
